import React, { useState, useEffect } from 'react';
import AppLayout from '../components/AppLayout';
import { FileText, Lock } from 'lucide-react';

const LegalSection = ({ title, children }) => (
    <div className="mb-12">
        <h3 className="font-mono text-sm text-[var(--fg-secondary)] uppercase border-b border-[var(--border-main)] pb-2 mb-4">
            {title}
        </h3>
        <div className="font-serif text-[var(--fg-primary)] text-sm leading-relaxed space-y-4">
            {children}
        </div>
    </div>
);

const LegalPage = () => {
    useEffect(() => {
        document.title = "AltFinder / Protocol Legal";
    }, []);

    const [activeDoc, setActiveDoc] = useState('tos'); // 'tos' or 'privacy'

    return (
        <AppLayout>
            <div className="flex h-full">
                {/* Sidebar Navigation */}
                <div className="w-64 border-r border-[var(--border-main)] bg-[var(--bg-panel)] p-6 pt-10">
                    <h2 className="font-mono text-[10px] text-[var(--fg-tertiary)] uppercase tracking-widest mb-6">Legal Documentation</h2>
                    <div className="space-y-2">
                        <button
                            onClick={() => setActiveDoc('tos')}
                            className={`w-full flex items-center gap-3 px-4 py-2 text-xs font-mono uppercase transition-colors border ${activeDoc === 'tos'
                                ? 'bg-[var(--bg-main)] border-[var(--fg-tertiary)] text-[var(--fg-primary)]'
                                : 'border-transparent text-[var(--fg-secondary)] hover:text-[var(--fg-primary)] hover:bg-[var(--bg-main)]'
                                }`}
                        >
                            <FileText size={14} /> TERMS_OF_SERVICE
                        </button>
                        <button
                            onClick={() => setActiveDoc('privacy')}
                            className={`w-full flex items-center gap-3 px-4 py-2 text-xs font-mono uppercase transition-colors border ${activeDoc === 'privacy'
                                ? 'bg-[var(--bg-main)] border-[var(--fg-tertiary)] text-[var(--fg-primary)]'
                                : 'border-transparent text-[var(--fg-secondary)] hover:text-[var(--fg-primary)] hover:bg-[var(--bg-main)]'
                                }`}
                        >
                            <Lock size={14} /> PRIVACY_POLICY
                        </button>
                    </div>
                </div>

                {/* Document Viewer */}
                <div className="flex-1 overflow-y-auto bg-[var(--bg-main)] p-12 lg:pr-32">
                    <div className="max-w-3xl mx-auto">
                        <div className="mb-10 text-center">
                            <h1 className="font-serif text-3xl text-[var(--fg-primary)] mb-2">
                                {activeDoc === 'tos' ? 'Terms of Service' : 'Privacy Policy'}
                            </h1>
                            <p className="font-mono text-xs text-[var(--fg-tertiary)] uppercase tracking-widest">
                                Last Revised: 2024-12-16 // Version 1.2
                            </p>
                        </div>

                        {activeDoc === 'tos' ? (
                            <>
                                <LegalSection title="01. Protocol Acceptance">
                                    <p>By accessing the AltFinder Index ("the Protocol"), you acknowledge and agree to the operating parameters defined herein. Usage is a privilege, not a right.</p>
                                </LegalSection>
                                <LegalSection title="02. Data Sovereignty">
                                    <p>The information provided in this index is for open dissemination. Users are granted a limited license to replicate, fork, and modify the dataset for personal or research use, provided proper attribution is maintained.</p>
                                </LegalSection>
                                <LegalSection title="03. Liability Disclaimer">
                                    <p>The Protocol is provided "as is". The maintainers assume no responsibility for software failures, data corruption, or existence dread caused by the tools listed herein.</p>
                                </LegalSection>
                            </>
                        ) : (
                            <>
                                <div className="border border-[var(--signal-green)] bg-[var(--signal-green)]/5 p-4 mb-8">
                                    <h4 className="font-mono text-xs text-[var(--signal-green)] font-bold uppercase mb-2">Zero-Knowledge Summary</h4>
                                    <ul className="list-disc list-inside font-mono text-xs text-[var(--signal-green)] space-y-1">
                                        <li>NO tracking pixel injection.</li>
                                        <li>NO third-party advertising networks.</li>
                                        <li>NO storage of personal identification markers.</li>
                                    </ul>
                                </div>

                                <LegalSection title="01. Data Collection">
                                    <p>The Protocol is stateless. We do not maintain user accounts or session logs. Your query data is processed ephemerally and is not retained.</p>
                                </LegalSection>
                                <LegalSection title="02. External Links">
                                    <p>The Index contains pointers to external networks. We cannot verify the privacy stance of third-party domains. Proceed with operational caution.</p>
                                </LegalSection>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </AppLayout>
    );
};

export default LegalPage;
